/**
 * Read models for stored form submissions, mirroring the backend SubmissionResource
 * (CheqMe.Server.Services.Forms.Abstractions/Submission). Sensitive values arrive masked unless fetched via the
 * reveal endpoint (which requires the SensitiveDataAccessor role); attachments arrive as metadata only.
 */

/** Mirrors the backend SubmissionStatus enum (serialised as its numeric value). */
export enum SubmissionStatus {
  Draft = 0,
  Submitted = 1,
  Approved = 2,
  Rejected = 3,
  Void = 4,
  Archived = 5
}

/** Mirrors the backend ReviewType enum (CheqMe.Server.Contracts.Forms; serialised as its numeric value). */
export enum ReviewType {
  Payroll = 0
}

/** A handled-review marker on a submission (its presence means "handled" for that type). */
export type SubmissionReview = {
  type: ReviewType;
  handledAt: string;
  handledBy: string;
};

export type SubmissionFieldValue = {
  fieldKey: string;
  isSensitive: boolean;
  isMasked: boolean;
  /** Masked string / typed value / null, or for a file field an object { blobId, fileName, contentType, sizeBytes }. */
  value: any;
  /**
   * Position in the submission's own-version definition (depth-first), server-stamped so values display in
   * definition order even after the form was later reordered. Fields dropped from that version sort last.
   */
  order?: number;
};

export type SubmissionGroup = {
  groupKey: string;
  index: number;
  values: SubmissionFieldValue[];
};

export type Submission = {
  id: string;
  formId: string;
  formVersion: number;
  employeeId: string;
  submittedAt?: string;
  status: SubmissionStatus;
  retainUntil?: string;
  retentionBasis: number;
  needsReview: boolean;
  reviewReason?: string;
  /** When the submission was flagged for review (UTC), or undefined. */
  flaggedAt?: string;
  /** Review due date (flaggedAt + 14 days), or undefined when not flagged. */
  reviewDeadline?: string;
  /**
   * When this answer stops being valid — `submittedAt + the form's Gültigkeitsdauer`. Undefined/null when the
   * form has no validity period ("unbegrenzt gültig") or the submission was never finalised.
   *
   * **Server-derived.** The gate and every submission projection go through the backend's one `SubmissionValidity`
   * helper; the UI only formats these values and must never re-derive the rule, or the surfaces will disagree.
   */
  expiresAt?: string | null;
  /** True once {@link expiresAt} has passed — the answer is stale and counts as flagged. Server-derived. */
  expired?: boolean;
  /** When this is a revision draft, the submission it revises. */
  revisionOfSubmissionId?: string;
  /** Set once a newer revision has replaced this submission (it's then history, not the active answer). */
  supersededBySubmissionId?: string;
  /** Reviews already handled on this submission. A {@link ReviewType} absent here means it's still open. */
  reviews?: SubmissionReview[];
  groups: SubmissionGroup[];
};

/** Attachment metadata shape carried in a file field's value. */
export type SubmissionFileRef = {
  blobId: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
};

/** Result of uploading an attachment to a draft (mirrors the backend FileUploadResult). */
export type FileUploadResult = {
  blobId: string;
  fieldKey: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
};

/** True when a field value is an attachment reference rather than a scalar. */
export function isFileRef(value: any): value is SubmissionFileRef {
  return value != null && typeof value === 'object' && 'blobId' in value;
}
